import { Check, Video, ArrowRight, ShieldCheck, Building2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, Eyebrow, SectionHeading } from "@/components/Reveal";
import { useDemoForm } from "@/components/DemoForm";

const TEAM_FEATURES = [
  "Role-specific documentation workflows",
  "Support for multidisciplinary community-based team roles",
  "PIE, SOAP and DAP workflow configuration where applicable",
  "Team-specific configuration",
  "Implementation & setup",
  "Team onboarding and live training",
  "VeraChart documentation resources",
  "Unique team license identification",
  "Licensed-team identification embedded in delivered materials",
  "Implementation support",
];

const ENTERPRISE_FEATURES = [
  "Multiple individually licensed teams",
  "Centralized organizational implementation",
  "Customized documentation requirements",
  "Team-specific configurations",
  "Leadership onboarding",
  "Organization-wide training coordination",
  "Implementation support",
  "Volume pricing where appropriate",
];

const LICENSE_EXAMPLES = [
  ["1 ACT team", "1 VeraChart team license"],
  ["3 ACT teams", "3 VeraChart team licenses"],
  ["8 ACT/PACT teams", "8 VeraChart team licenses"],
];

export function Pricing() {
  const { openDialog } = useDemoForm();

  return (
    <section id="pricing" className="bg-slate-50 py-20 lg:py-28" aria-labelledby="pricing-heading">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <Eyebrow>Pricing</Eyebrow>
          <SectionHeading id="pricing-heading">VeraChart Team Licensing</SectionHeading>
          <p className="mt-5 text-base leading-relaxed text-slate-600">
            A professional documentation workflow, licensed to each team — with implementation, training, and
            uniquely identified licensed materials included.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-6 lg:grid-cols-2" data-testid="pricing-cards">
          <Reveal>
            <div className="flex h-full flex-col rounded-2xl bg-navy p-8 text-white shadow-xl shadow-navy/20 ring-1 ring-navy sm:p-10" data-testid="pricing-team-license">
              <span className="mb-4 self-start rounded-full bg-teal-600 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-white">
                Per-Team License
              </span>
              <h3 className="font-display text-2xl font-bold tracking-tight">VeraChart ACT Team License</h3>
              <p className="mt-3 font-mono text-3xl font-bold text-teal-400">
                Starting at $1,500 <span className="text-base font-semibold text-slate-300">per team</span>
              </p>
              <p className="mt-4 flex items-start gap-2 rounded-lg bg-teal-600/15 p-3.5 text-sm font-semibold text-teal-300">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                Licensed individually to each ACT, PACT, FACT, CSP, or similar community-based team.
              </p>
              <p className="mt-4 text-sm leading-relaxed text-slate-300">
                VeraChart is licensed at the individual team level. Each purchased team license provides that specific
                team with access to its configured VeraChart documentation workflow and role-specific resources. A
                license purchased for one team does not automatically license additional teams within the same
                organization.
              </p>
              <ul className="mt-6 flex-1 space-y-2.5">
                {TEAM_FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-slate-200">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-teal-500" aria-hidden="true" />
                    {f}
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  data-testid="pricing-team-demo-button"
                  onClick={() => openDialog("pricing-team-license", "Requesting a team demo — VeraChart ACT Team License (starting at $1,500 per team).")}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-teal-600 px-6 py-3.5 font-display text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-teal-500"
                >
                  Request Team Demo <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  data-testid="pricing-discuss-licensing-button"
                  onClick={() => openDialog("pricing-discuss-licensing", "We'd like to discuss team licensing for our program.")}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-slate-500 px-6 py-3.5 font-display text-sm font-semibold text-slate-200 transition-colors hover:border-teal-400 hover:text-white"
                >
                  Discuss Team Licensing
                </button>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-8 transition-all hover:-translate-y-1 hover:border-teal-300 hover:shadow-lg sm:p-10" data-testid="pricing-enterprise">
              <span className="mb-4 self-start rounded-full bg-teal-50 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-teal-700">
                Multi-Team &amp; Enterprise
              </span>
              <h3 className="font-display text-2xl font-bold tracking-tight text-navy">Multi-Team &amp; Enterprise</h3>
              <p className="mt-3 font-mono text-3xl font-bold text-teal-700">Custom Pricing</p>
              <p className="mt-4 text-sm leading-relaxed text-slate-600">
                For behavioral-health organizations operating multiple community-based teams — ACT, PACT, FACT, CSP, and similar programs. Each participating
                team receives its own VeraChart team license and unique licensing identification.
              </p>
              <p className="mt-4 flex items-start gap-2 text-sm font-medium text-navy">
                <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" aria-hidden="true" />
                Potential multi-team agreements may include:
              </p>
              <ul className="mt-4 flex-1 space-y-2.5">
                {ENTERPRISE_FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" aria-hidden="true" />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                data-testid="pricing-enterprise-button"
                onClick={() => openDialog("pricing-enterprise", "We operate multiple teams and would like to discuss multi-team/enterprise licensing.")}
                className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 font-display text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-teal-700"
              >
                Discuss Multi-Team Licensing <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-14 rounded-2xl border border-slate-200 bg-white p-8 sm:p-10" data-testid="one-license-one-team">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.22em] text-teal-700">How Licensing Works</p>
            <h3 className="mt-3 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">One License. One Team.</h3>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-600">
              VeraChart licenses are issued to individual community-based teams — ACT, PACT, FACT, CSP, and similar programs. If an organization operates
              multiple teams, each team requires its own license.
            </p>
            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              {LICENSE_EXAMPLES.map(([teams, licenses]) => (
                <div key={teams} className="rounded-xl bg-slate-50 p-5 text-center ring-1 ring-slate-200">
                  <p className="font-mono text-lg font-bold text-navy">{teams}</p>
                  <p className="my-1.5 font-mono text-teal-600" aria-hidden="true">=</p>
                  <p className="font-mono text-lg font-bold text-teal-700">{licenses}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm leading-relaxed text-slate-600">
              This licensing structure allows VeraChart to configure, identify, support, and maintain each team's
              documentation workflow independently. Multi-team organizations should contact VeraChart for customized
              implementation and volume pricing.
            </p>
            <p className="mt-4 border-t border-slate-100 pt-4 text-xs leading-relaxed text-slate-500">
              <strong className="text-navy">VeraChart Team License.</strong> A VeraChart Team License authorizes use
              of the licensed VeraChart materials by the specifically identified team within the purchasing
              organization. Additional teams require separate licenses unless otherwise provided under a written
              multi-team or enterprise agreement. License credentials and licensed materials may not be transferred
              to another team or organization without authorization from VeraChart.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <p className="mt-8 flex items-start gap-2.5 rounded-xl border border-slate-200 bg-white p-5 text-sm text-slate-600" data-testid="pricing-onboarding-note">
            <Video className="mt-0.5 h-5 w-5 shrink-0 text-teal-600" aria-hidden="true" />
            Every team license includes live implementation and training via Zoom or Microsoft Teams.{" "}
            <Link to="/onboarding" data-testid="pricing-onboarding-link" className="ml-1 font-semibold text-teal-700 underline underline-offset-2 hover:text-navy">
              Licensed already? Complete your team's onboarding questionnaire →
            </Link>
          </p>
        </Reveal>
      </div>
    </section>
  );
}
